import { Fragment, useEffect, useState } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, BellIcon, XMarkIcon, ExclamationTriangleIcon, BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { NavLink } from "react-router-dom";
import { Link } from "react-router-dom";
import Text from "../../img/sammi-navbar-logo.png";
import { logout } from "../../utils/apiUtils";
import { JobNotification } from "../../models/JobNotification";
import ConfirmOperatorModal from "../common/ConfirmOperatorModal";
import { useConfig } from "../common/config-context/ConfigContext";
import LoginService from "../../services/login.service";
import { useTranslation } from "react-i18next";
import JobsService from "../../services/job.service";
import { Job } from "../../types/Job";
import { toastSuccess } from "../../App";
import SuperAdminService, { Organization } from "../../services/super-admin.service";

type NavigationItem = {
  name: string;
  href: string;
  current: boolean;
};

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
  const [navigations, setNavigations] = useState<NavigationItem[]>([]);
  const [notifications, setNotifications] = useState<JobNotification[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<JobNotification | null>(null);
  const [initials, setInitials] = useState<string>("...");
  const [email, setEmail] = useState<string>("");
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string>("");
  const [currentOrgName, setCurrentOrgName] = useState<string>("");
  const [hasSelectedOrg, setHasSelectedOrg] = useState<boolean>(false);
  const config = useConfig();
  const getSiteLabel = config?.siteTitle || 'Sites';
  const { t } = useTranslation();

  const fetchNotifications = async () => {
    const pendingManagerJobs = await JobsService.getPendingJobs();
    const jobs = await pendingManagerJobs.json();

    if (pendingManagerJobs.ok) {
      const notifications: JobNotification[] = jobs.map((job: Job) => ({
        jobId: job._id,
        title: `Pending Job`,
        description: 'You have a new job to approve.',
        createdAt: job.createdAt ? new Date(job.createdAt) : new Date(),
      }));

      setNotifications(notifications);
    }
  };

  const requestPasswordReset = async () => {
    const response = await LoginService.requestPasswordReset(email);

    if (response.ok) {
      toastSuccess(t('nav.resetPasswordMsg'));
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await SuperAdminService.getOrganizations();
      if (response.ok) {
        const orgs = await response.json();
        setOrganizations(orgs);
        
        // If we have currentOrgId but no name, find it from the orgs list
        const accessToken = localStorage.getItem('accessToken');
        const decodedToken = accessToken ? JSON.parse(atob(accessToken.split('.')[1])) : null;
        if (decodedToken && decodedToken.organizationId) {
          const currentOrg = orgs.find((org: Organization) => org._id === decodedToken.organizationId);
          if (currentOrg) {
            setCurrentOrgName(currentOrg.name);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    }
  };

  const fetchCurrentOrganization = async () => {
    try {
      const response = await SuperAdminService.getCurrentOrganization();
      if (response.ok) {
        const data = await response.json();
        // Handle the actual API response structure: { organization: { _id, name, ... }, hasSelectedOrganization }
        if (data.organization) {
          setCurrentOrgId(data.organization._id);
          setCurrentOrgName(data.organization.name);
          // Check both API flag and localStorage flag
          const hasSelectedFromAPI = data.hasSelectedOrganization || false;
          const hasSelectedFromStorage = localStorage.getItem('superAdminSelectedOrg') === 'true';
          setHasSelectedOrg(hasSelectedFromAPI || hasSelectedFromStorage);
        }
      }
    } catch (error) {
      console.error("Failed to fetch current organization:", error);
    }
  };

  const handleSelectOrganization = async (organizationId: string) => {
    try {
      const response = await SuperAdminService.selectOrganization(organizationId);
      if (response.ok) {
        const data = await response.json();
        // Update the access token with the new one that has the selected org
        if (data.access_token) {
          localStorage.setItem('accessToken', data.access_token);
          // Mark that we've selected a different organization
          localStorage.setItem('superAdminSelectedOrg', 'true');
          toastSuccess('Organization selected successfully');
          // Refresh the page to update all components with new organization context
          window.location.reload();
        }
      }
    } catch (error) {
      console.error("Failed to select organization:", error);
    }
  };

  const handleClearOrganization = async () => {
    try {
      const response = await SuperAdminService.clearOrganization();
      if (response.ok) {
        const data = await response.json();
        // Update the access token with the new one that has the original org
        if (data.access_token) {
          localStorage.setItem('accessToken', data.access_token);
          // Clear the flag since we're back to original org
          localStorage.removeItem('superAdminSelectedOrg');
          toastSuccess('Organization selection cleared');
          // Refresh the page to update all components with original organization context
          window.location.reload();
        }
      }
    } catch (error) {
      console.error("Failed to clear organization:", error);
    }
  };

  const chipValue = () => {
    if (notifications.length > 0) {
      if (notifications.length > 99) {
        return "99+";
      }
      return notifications.length;
    }
    return 0;
  }

  const getSelectedJobId = () => {
    if (selectedNotification) {
      const selectedJobIdasd = selectedNotification.jobId;

      return selectedJobIdasd;
    }
    return null;
  }

  useEffect(() => {
    setNavigations([
      // Temporary commented out until we have a dashboard
      // { name: t('nav.dashboard'), href: "/", current: true },
      { name: t('nav.map'), href: "/map", current: true },
      { name: t('nav.jobs'), href: "/jobs", current: false },
      { name: t('nav.sites'), href: "/sites", current: false },
      { name: t('nav.monitors'), href: "/monitors", current: false },
      { name: t('nav.fleets'), href: "/fleets", current: false },
      { name: t('nav.staff'), href: "/staff", current: false },
      // { name: t('nav.inbox'), href: "/inbox", current: false },
    ]);

    const accessToken = localStorage.getItem('accessToken');
    const decodedToken = accessToken ? JSON.parse(atob(accessToken.split('.')[1])) : null;

    if (decodedToken) {
      setInitials(decodedToken.firstName.charAt(0) + decodedToken.lastName.charAt(0));
      setEmail(decodedToken.email);
      setIsSuperAdmin(decodedToken.isSuperAdmin || false);
      
      // If user is super admin, fetch organizations and current org
      if (decodedToken.isSuperAdmin) {
        // Set current org from JWT token immediately
        if (decodedToken.organizationId) {
          setCurrentOrgId(decodedToken.organizationId);
        }
        
        fetchOrganizations();
        fetchCurrentOrganization();
      }
    }

    fetchNotifications();
    const interval = setInterval(() => {
      fetchNotifications();
    }, 10 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Disclosure as="nav" className="bg-black">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
              <div className="relative flex h-16 items-center justify-between">
                <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                  {/* Mobile menu button*/}
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
                <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                  <div className="flex flex-shrink-0 items-center">
                    <img
                      className="block h-10 w-auto lg:hidden"
                      src={Text}
                      alt="Your Company"
                    />
                    <img
                      className="hidden h-10 w-auto lg:block"
                      src={Text}
                      alt="Your Company"
                    />
                  </div>
                  <div className="hidden sm:ml-6 sm:block">
                    <div className="flex space-x-4">
                      {navigations.map((item, i) => (
                        <NavLink
                          key={i}
                          to={item.href}
                          className={({ isActive, isPending }) =>
                            classNames(
                              isActive
                                ? "bg-gray-800 text-white"
                                : "text-gray-300 hover:bg-gray-700 hover:text-white",
                              "px-3 py-2 rounded-md text-sm font-medium"
                            )
                          }
                        >
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                  {/* Super Admin Organization Selector */}
                  {isSuperAdmin && (
                    <Menu as="div" className="relative ml-3">
                      <div>
                        <Menu.Button className="flex items-center rounded-md bg-gray-800 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                          <BuildingOfficeIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                          <span className="hidden md:inline">
                            {currentOrgName || "Select Organization"}
                          </span>
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-96 overflow-y-auto">
                          {currentOrgId && currentOrgName && (
                            <Menu.Item>
                              {({ active }) => (
                                <div className="px-4 py-2 text-xs text-gray-500 border-b">
                                  Current: {currentOrgName}
                                </div>
                              )}
                            </Menu.Item>
                          )}
                          {hasSelectedOrg && (
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={handleClearOrganization}
                                  className={classNames(
                                    active ? "bg-gray-100" : "",
                                    "block w-full text-left px-4 py-2 text-sm text-red-600 border-b"
                                  )}
                                >
                                  Clear Selection
                                </button>
                              )}
                            </Menu.Item>
                          )}
                          {organizations.length === 0 && (
                            <Menu.Item>
                              {({ active }) => (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  No organizations available
                                </div>
                              )}
                            </Menu.Item>
                          )}
                          {organizations.map((org) => (
                            <Menu.Item key={org._id}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleSelectOrganization(org._id)}
                                  className={classNames(
                                    active ? "bg-gray-100" : "",
                                    currentOrgId === org._id ? "bg-blue-50" : "",
                                    "block w-full text-left px-4 py-2 text-sm text-gray-700"
                                  )}
                                >
                                  {org.name}
                                  {currentOrgId === org._id && (
                                    <span className="ml-2 text-xs text-blue-600">✓</span>
                                  )}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  )}

                  <Menu as="div" className="relative ml-3">
                    <div>
                      <Menu.Button className="flex rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                        <span className="sr-only">{t('nav.viewNotifications')}</span>
                        <BellIcon className="h-6 w-6" aria-hidden="true" />
                        <span className="absolute top-0 -right-2 -mt-3 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">{chipValue()}</span>
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {notifications.length === 0 && (
                          <Menu.Item>
                            {({ active }) => (
                              <div className="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-3 rounded relative" role="alert">
                                <span className="block sm:inline text-black">{t('nav.noNotifications')}</span>
                              </div>
                            )}
                          </Menu.Item>
                        )}

                        {notifications.map((notification, i) => (
                          <Menu.Item key={i}>
                            {({ active }) => (
                              <div onClick={() => { setSelectedNotification(notification) }} className="px-4 py-3 rounded relative" role="alert">
                                <span className="block sm:inline text-black">{notification.title}</span>
                                <span className="absolute top-0 bottom-0 right-0 px-4 py-3 text-black">
                                  <ExclamationTriangleIcon className="h-6 w-6 mr-2" aria-hidden="true" />
                                </span>
                                <p className="text-sm text-black">{notification.description}</p>
                              </div>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>

                  {/* Profile dropdown */}
                  <Menu as="div" className="relative ml-3">
                    <div>
                      <Menu.Button className="flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                        <span className="sr-only">Open user menu</span>
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500">
                          <span className="text-lg font-medium leading-none text-white">
                            {initials}
                          </span>
                        </span>
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to={"setting/company"}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              {t('nav.settings')}
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <span
                              onClick={requestPasswordReset}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              {t('nav.resetPassword')}
                            </span>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="/login"
                              onClick={logout}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              {t('nav.signOut')}
                            </a>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden">
              <div className="space-y-1 px-2 pt-2 pb-3">
                {navigations.map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as="a"
                    href={item.href}
                    className={classNames(
                      item.current
                        ? "bg-gray-900 text-white"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white",
                      "block px-3 py-2 rounded-md text-base font-medium"
                    )}
                    aria-current={item.current ? "page" : undefined}
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      <ConfirmOperatorModal jobId={getSelectedJobId()} onClose={() => { setSelectedNotification(null) }} ></ConfirmOperatorModal>
    </>
  );
}
